import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { downloadCSV, getNewSort } from '~/utils/common';
import { updateLocationSearchParams } from '~/utils/formatter';
import themes, { styled } from '~/themes';
import { useGetTasks } from '~/pages/LiveTracking/apis';
import Button from '~/components/common/Button';
import {
  cleanDeep,
  fromQueryString,
  omitFromQueryString,
  toQueryString,
} from '~/utils/queryString';
import { Stack, Typography, useMediaQuery } from '@mui/material';
import { useAuth } from '~/providers/AuthProvider';
import Tutorial from '~/components/common/Tutorial';
import { TutorialData } from '~/constants/tutorial';
import Tabs from '~/components/common/Tabs';
import { ITabValue } from '~/models/shared';
import { ITaskResponse } from '~/pages/LiveTracking/types';
import { useCheckRefreshTask, useFirstLoadingDetection } from '~/hooks';
import { StyledPanelDetailContainer } from '~/themes/style';
import { usePaymentInfo } from '~/providers/PaymentProvider';
import { PageLayout } from '~/layout/PageLayout';
import { useKeyDow } from '~/hooks/useKeyDown';
import { TaskDetail } from '~/pages/LiveTracking/components/TaskDetail';
import { Pagination } from '~/components/common/Pagination';
import ExportIcon from '~/assets/images/icons/export.svg';
import ImportIcon from '~/assets/images/icons/import.svg';
import AddIcon from '~/assets/images/icons/add-white.svg';
import { useExportTasks } from '~/services/api/task';
import { useGetAllIntegrationQuery } from '~/services/api/stores';
import { DEFAULT_TIMEZONE } from '~/constants/common';
import momentTz from 'moment-timezone';
import { TIMEZONE_SETTING } from '~/constants/adminOptions';
import { useIsMobileLandscape } from '~/utils/hooks/useIsMobileLandscape';
import { chain, isArray, isEmpty, isNil, isString, trim } from 'lodash';
import { FilterForm } from './FilterForm';
import { TaskTable } from './TaskTable';
import SetupYourTask from './SetupYourTask';
import { ImportTask } from './ImportTask';
import { DialogAddTask } from '../LiveTracking/components/AddTask';

export const StyledHeaderButtonContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  paddingBottom: theme.spacing(2),
  gap: theme.spacing(0.8),
  [theme.breakpoints.down('md')]: {
    paddingBottom: 0,
    marginTop: theme.spacing(1),
  },
}));

type ITaskListProps = {};

export const TaskList: React.FC<ITaskListProps> = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isSmDown = useMediaQuery(themes.breakpoints.down('sm'));
  const isLandscape = useIsMobileLandscape();

  const { account, getTooltipOpen, updateUserTooltip } = useAuth();
  const { org, setIsOpenUpSellDialog, isStarterPlan } = usePaymentInfo();
  const [openCreateTaskDialog, setOpenCreateTaskDialog] = useState(false);
  const [openImportTaskDialog, setOpenImportTaskDialog] = useState(false);
  const [tabCounts, setTabCounts] = useState({
    ongoing: null,
    completed: null,
  });

  const timezone = useMemo(() => {
    if (account.timezone) {
      if (account.timezone === TIMEZONE_SETTING.AUTO_DETECT) {
        let tz = momentTz.tz.guess();
        if (tz === 'Asia/Saigon') {
          tz = 'Asia/Ho_Chi_Minh';
        }
        return tz;
      }
      return account.timezone;
    }
    return DEFAULT_TIMEZONE;
  }, [account.org]);

  const itemPerPage: number = account?.items_per_page || 10;

  const allQuery = useMemo(
    () => fromQueryString(location.search, { arrayFormat: 'none' }),
    [location.search],
  );

  const { tab, ...extraLocationSearch } = allQuery;
  const {
    id,
    search,
    statuses,
    executor_ids,
    preferred_team_ids,
    page: p,
    sort,
    sort_by,
    reassigned_status,
    date_gte,
    length,
    created_by,
    name,
    pickup_location,
    recipient,
    dropoff_location,
    rating,
    schedule_time_from,
    schedule_time_to,
    schedule_date_from,
    schedule_date_to,
    pickedup_time_from,
    pickedup_time_to,
    pickedup_date_from,
    pickedup_date_to,
    complete_time_from,
    complete_time_to,
    complete_date_from,
    complete_date_to,
  } = extraLocationSearch || {};
  const tabValue = (tab || ITabValue.Ongoing) as ITabValue;
  const taskId = id as string;
  const page = (p || 1) as string;

  const { data: listStore } = useGetAllIntegrationQuery();
  const listActiveStore = useMemo(
    () => listStore?.filter((store) => store.status === 'active'),
    [listStore],
  );

  const {
    hasUpdate,
    resetUpdate,
    hasConnected,
    forceUpdate: forceUpdateTask,
  } = useCheckRefreshTask();

  const {
    data,
    isFetching: isFetchingTask,
    isPreviousData,
  } = useGetTasks({
    params: {
      search,
      statuses: statuses || tabValue,
      executor_ids,
      preferred_team_ids,
      page,
      limit: itemPerPage,
      sort,
      sort_by,
      reassigned_status,
      date_gte,
      length,
      timezone,
      created_by,
      name,
      pickup_location,
      recipient,
      dropoff_location,
      rating,
      schedule_time_from,
      schedule_time_to,
      schedule_date_from,
      schedule_date_to,
      pickedup_time_from,
      pickedup_time_to,
      pickedup_date_from,
      pickedup_date_to,
      complete_time_from,
      complete_time_to,
      complete_date_from,
      complete_date_to,
    },
    enabled: !!account && (hasUpdate || !hasConnected),
    refetchInterval: 7 * 1000,
    onSuccess: (resp: ITaskResponse) => {
      resetUpdate();
      const newTabCounts =
        tabValue === ITabValue.Ongoing
          ? {
              ongoing: resp.total_items,
              completed: resp.done_list,
            }
          : {
              ongoing: resp.not_done_list,
              completed: resp.total_items,
            };
      setTabCounts(newTabCounts);
    },
  });

  useEffect(() => {
    forceUpdateTask();
  }, [allQuery]);

  const isEmptySearch = chain(extraLocationSearch)
    .omit(['page', 'limit'])
    .values()
    .every(
      (value) =>
        isNil(value) ||
        (isString(value) && trim(value) === '') ||
        (isArray(value) && isEmpty(value)),
    )
    .value();

  const handleDownloadCSV = (csvString: string) => {
    let fileName = 'Export_All_Task_List';
    if (!isEmptySearch) {
      fileName = 'Export_Filter_Task_List';
    }
    downloadCSV(csvString, fileName);
  };

  const { mutate: exportAllTaskMutation, isLoading: isLoadingExportAllTask } =
    useExportTasks({
      onSuccess: (csvData) => {
        handleDownloadCSV(csvData?.csv);
      },
    });

  const isShowTaskListLoading = !(!isFetchingTask || !isPreviousData);
  const isFirstLoading = useFirstLoadingDetection([isFetchingTask]);

  const isFullEmpty = useMemo(() => {
    if (isFirstLoading) return true;
    if (tabValue === ITabValue.Completed)
      return data?.done_list === 0 && !Object.keys(extraLocationSearch).length;
    return (
      data?.not_done_list === 0 && !Object.keys(extraLocationSearch).length
    );
  }, [tabValue, data, isFirstLoading]);

  const { total_pages } = data || {};

  const tasks = useMemo(
    () => (data?.items.length ? data.items.filter((t) => !t.isDivider) : []),
    [data],
  );

  const onSubmit = useCallback(
    (values: any) => {
      cleanDeep(values);
      navigate({
        pathname: location.pathname,
        search: toQueryString({
          ...values,
          page: 1,
          tab,
        }),
      });
    },
    [location],
  );

  const onClear = useCallback(() => {
    navigate({
      pathname: location.pathname,
      search: omitFromQueryString(location.search, ['search']),
    });
  }, [location]);

  const onClearAll = useCallback(() => {
    navigate({
      pathname: location.pathname,
      search: `?tab=${tabValue}`,
    });
  }, [location]);

  const handleSort = useCallback(
    (sortId: string, nextArrange: number) => {
      const sortParams = getNewSort(sortId, nextArrange);
      navigate({
        pathname: location.pathname,
        search: `?${updateLocationSearchParams(location, sortParams)}`,
      });
    },
    [location, sort, sort_by],
  );

  const onClose = useCallback(() => {
    navigate({
      pathname: location.pathname,
      search: omitFromQueryString(location.search, 'id'),
    });
  }, [location]);

  const handleCloseCreateTaskDialog = useCallback(() => {
    setOpenCreateTaskDialog(false);
  }, []);

  useKeyDow({
    targetKey: 'Escape',
    disabled: !taskId,
    callback: onClose,
  });

  const isEmptyStateTable = useMemo<boolean>(() => {
    const queries = fromQueryString(location.search);
    cleanDeep(queries);
    const s = toQueryString(queries);

    return (
      tabValue === ITabValue.Ongoing &&
      data?.items?.length === 0 &&
      !omitFromQueryString(s, 'tab')
    );
  }, [location, tabValue, data]);

  const headerTitle = useMemo(
    () => (
      <Tutorial
        isOpen={getTooltipOpen(TutorialData.TaskList.id)}
        onClose={() =>
          updateUserTooltip({
            [TutorialData.TaskList.id]: true,
          })
        }
        sx={{ maxWidth: 100, display: { xs: 'none', md: 'block' } }}
        {...TutorialData.TaskList}
      >
        <Typography variant='h2' data-testid='task-list-title'>
          Task List
        </Typography>
      </Tutorial>
    ),
    [],
  );

  const headerButtons = useMemo(() => {
    const isOpenTutorial = isFirstLoading
      ? false
      : getTooltipOpen(TutorialData.CreateTask.id);

    return (
      <StyledHeaderButtonContainer>
        <Button
          noRounder
          buttonType='text'
          onClick={() => {
            exportAllTaskMutation({
              statuses: statuses ? [statuses] : tabValue,
              ...(executor_ids && { executor_ids: [executor_ids] }),
              ...(search && { search }),
              preferred_team_ids,
              sort,
              sort_by,
              reassigned_status,
              date_gte,
              timezone,
              created_by,
              name,
              pickup_location,
              recipient,
              dropoff_location,
              rating,
              schedule_time_from,
              schedule_time_to,
              schedule_date_from,
              schedule_date_to,
              pickedup_time_from,
              pickedup_time_to,
              pickedup_date_from,
              pickedup_date_to,
              complete_time_from,
              complete_time_to,
              complete_date_from,
              complete_date_to,
            });
          }}
          loading={isLoadingExportAllTask}
          sx={{
            [themes.breakpoints.down('md')]: {
              minWidth: 'unset !important',
            },
          }}
        >
          <Typography
            variant='body1'
            sx={{
              cursor: 'pointer',
              color: themes.color.violet500,
              fontWeight: 500,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.4,
            }}
          >
            <img src={ExportIcon} alt='export-icon' /> Export List
          </Typography>
        </Button>
        <Button
          buttonType='text'
          noRounder
          onClick={() => {
            setOpenImportTaskDialog(true);
          }}
          sx={{
            [themes.breakpoints.down('md')]: {
              minWidth: 'unset !important',
            },
          }}
        >
          <Typography
            variant='body1'
            sx={{
              cursor: 'pointer',
              color: themes.color.violet500,
              fontWeight: 500,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.4,
            }}
          >
            <img src={ImportIcon} alt='import-icon' /> Import Task
          </Typography>
        </Button>

        <Tutorial
          isOpen={isOpenTutorial}
          onClose={() =>
            updateUserTooltip({
              [TutorialData.CreateTask.id]: true,
            })
          }
          {...TutorialData.CreateTask}
        >
          <Button
            noRounder
            buttonType='primary-dark'
            onClick={() => {
              if (
                isStarterPlan &&
                org?.monthly_created_task >= org?.limit_task
              ) {
                return setIsOpenUpSellDialog(true);
              }
              return setOpenCreateTaskDialog(true);
            }}
            sx={{
              [themes.breakpoints.down('md')]: {
                minWidth: 'unset !important',
              },
            }}
          >
            <Typography
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.4,
              }}
            >
              <img src={AddIcon} alt='add-icon' /> Add Task
            </Typography>
          </Button>
        </Tutorial>
      </StyledHeaderButtonContainer>
    );
  }, [
    isFirstLoading,
    isStarterPlan,
    isLoadingExportAllTask,
    statuses,
    tabValue,
    executor_ids,
    search,
    preferred_team_ids,
    sort,
    sort_by,
    reassigned_status,
    date_gte,
    timezone,
    created_by,
    name,
    pickup_location,
    recipient,
    dropoff_location,
    rating,
    schedule_time_from,
    schedule_time_to,
    schedule_date_from,
    schedule_date_to,
    pickedup_time_from,
    pickedup_time_to,
    pickedup_date_from,
    pickedup_date_to,
    complete_time_from,
    complete_time_to,
    complete_date_from,
    complete_date_to,
  ]);

  const tabs = useMemo(
    () => (
      <Tabs
        tabs={[
          {
            value: ITabValue.Ongoing,
            label: 'Ongoing',
            count: tabCounts.ongoing || 0,
          },
          {
            value: ITabValue.Completed,
            label: 'Completed',
            count: tabCounts.completed || 0,
          },
        ]}
        countWithoutBackground
        value={tabValue}
        onChange={(val) =>
          navigate({
            pathname: location.pathname,
            search: toQueryString({ tab: val }),
          })
        }
      />
    ),
    [location, tabCounts, tabValue],
  );

  const renderTable = () => (
    <TaskTable
      timezone={timezone}
      data={tasks}
      stores={listStore}
      isMultiStore={listActiveStore?.length > 1}
      handleSort={handleSort}
      isEmptyState={isEmptyStateTable}
      isLoading={isShowTaskListLoading}
      isFullEmpty={isFullEmpty}
      renderEmpty={
        data?.done_list === 0 &&
        data?.not_done_list === 0 &&
        !isFirstLoading ? (
          <SetupYourTask
            setOpenCreateTask={setOpenCreateTaskDialog}
            setOpenImportTask={setOpenImportTaskDialog}
          />
        ) : null
      }
    />
  );

  const renderTaskListBody = () => (
    <>
      <Stack
        sx={{
          marginBottom: themes.spacing(2.4),
          [themes.breakpoints.down('md')]: {
            marginBottom: themes.spacing(1.5),
          },
        }}
      >
        <FilterForm
          onSubmit={onSubmit}
          onClear={onClear}
          onClearAll={onClearAll}
          listStore={listActiveStore}
        />
      </Stack>
      {isSmDown || isLandscape ? (
        <Stack
          style={{
            minHeight: 'max(calc(100vh - 468px), 400px)',
          }}
        >
          {renderTable()}
        </Stack>
      ) : (
        renderTable()
      )}
      {!!tasks.length && (
        <Stack sx={{ pt: 2, alignItems: 'flex-end' }}>
          <Pagination
            totalPages={total_pages}
            currentPage={parseInt(page, 10)}
          />
        </Stack>
      )}
    </>
  );

  const renderTaskDetail = useMemo(() => {
    if (!taskId) return '';
    return (
      <TaskDetail
        taskId={taskId}
        handleCloseTaskDetail={onClose}
        containerStyles={{
          position: 'unset',
          width: '100% !important',
          height: '100% !important',
          maxWidth: 'unset !important',
        }}
      />
    );
  }, [taskId]);

  return (
    <>
      <PageLayout
        headerTitle={headerTitle}
        isFistLoading={isFirstLoading}
        sxHeaderContainer={{
          [themes.breakpoints.down('md')]: {
            flexDirection: 'column',
            alignItems: 'center',
          },
          alignItems: 'flex-start',
          pt: themes.spacing(2.4),
          px: themes.spacing(2.4),
        }}
        sxContentContainer={{
          pt: themes.spacing(2.4),
          px: themes.spacing(2.4),
        }}
        sxHeaderLeftContainer={{
          'h2': {
            color: themes.color.violet700,
            lineHeight: '35px',
          },
          '& .MuiTabs-root': {
            '& .MuiTabs-indicator': {
              backgroundColor: `${themes.color.violet700} !important`,
            },
          },
          '& .MuiTab-root': {
            'padding': 0,
            '& .MuiTabs-indicator': {
              backgroundColor: `${themes.color.violet700} !important`,
            },
          },
          '& .MuiTabs-flexContainer': {
            gap: 2,
          },
          '& .MuiButtonBase-root': {
            'textTransform': 'unset',
            '& span': {
              color: `rgba(51, 54, 61, 0.40)`,
              fontWeight: 600,
            },
          },
          '& .Mui-selected': {
            '& span': {
              color: `${themes.color.violet500}`,
              fontWeight: 700,
            },
          },
        }}
        renderHeaderButtons={headerButtons}
        renderTabs={tabs}
      >
        {renderTaskListBody()}
      </PageLayout>
      {openCreateTaskDialog && (
        <DialogAddTask open onClose={handleCloseCreateTaskDialog} />
      )}

      {openImportTaskDialog && (
        <ImportTask
          open={openImportTaskDialog}
          setOpen={setOpenImportTaskDialog}
        />
      )}
      <StyledPanelDetailContainer
        role='presentation'
        isOpen={!!taskId}
        fixedWidth={480}
      >
        {renderTaskDetail}
      </StyledPanelDetailContainer>
    </>
  );
};
